/**
  * @class mmodOfficeWfConfigurations
  * @description
  * Configurations module allows administrator to manage configurations for Mosy applications.
  * Each application must have assigned a configuration.
  * Configuration is usually limited for only particular types of applications,
  * as different types have different needs for config.
  * When new application is enrolled, it uses by default configuration that has
  * the name same as application type. Functionalities of administration include:
  * - create new configuration
  * - update configuration
  * - disable configuration
  * - delete configuration
  * Module is implemented as workflow wfConfigurations.
  * Server class is msvrWfConfigurations, but it’s listener_id is msvrConfigurations.
  * It does not do any special handling, just includes workflow definitions.
  * On client side, there is mmodOfficeWfConfigurations module, which is derived from mmodWorkflow.
  * It does all usual workflow client processing with one small tweak - update of configuration translations.
  */

import mmodWorkflow from 'mmodWorkflow'

export default class mmodOfficeWfConfigurations extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      workflow_server: { type: 'string', default: ':msvrConfigurations', description: 'Listener acting as the server-side for the workflow' },
      tabular: { type: 'boolean', description: 'if true, module will work in filter+table mode, otherwise in list mode', default: true },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    if(params.type=='configurations') {
      // when watcher update came, update translations of configuration id to configuration name
      if(params.full) {
        LANG.createOptions('configuration.name.', { '': LANG.ZERO_SPACE }, true)
      }

      params.list.forEach(configuration=>{
        LANG.createOptions('configuration.name.', { [configuration.id]: configuration.name })
      })

      try{
        document.body.forAll('.mele-list[auto][prefix="configuration.name."]', el=>el.autoChanged(true))
        document.body.forAll('.mele-input[prefix="configuration.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('mele-button[prefix="configuration.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('.mele-table.configuration-dependent', el=>el.rowattributesChanged())
      }catch(e) {}
    }
    super.mmObjects(sender, params)
  } // mmObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * workflow model definition
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - workflow ID
  * @param {integer} [params.version] - model version
  * @param {string} [params.icon] - SVG icon
  * @param {string} [params.md5] - MD5 checksum
  * @param {string} [params.id_generator] - indicates who and how generates id
  * @param {string} [params.sqlview] - definition of SQL query to retrieve read-only workflow
  * @param {string} [params.sqlview_log] - definition of SQL query to retrieve read-only workflow logs
  * @param {string} [params.notifications] - indicates whether to notify on object changes
  * @param {object} [params.fields] - workflow variables
  * @param {object} [params.sql_queries] - SQL queries used for calculation of fields
  * @param {object} [params.forms] - workflow GUI forms
  * @param {string} [params.translations] - workflow translations as CSV
  * @param {string} [params.scripts] - workflow scripts
  * @param {string} [params.styles] - workflow CSS styles
  * @param {array} [params.acls] - access control list
  * @param {object} [params.states] - workflow states and their actions
  */
  mmModel(sender, params) {
    super.mmModel(sender, params)
    this.publish(sender, 'mmWatch', { version: 0 })
  } // mmModel
} // mmodOfficeWfConfigurations
